import { SiteUrls } from '@tableau/shared-urls';
import * as _ from 'underscore';
import { IAuthenticationProviderSelectorModel } from '../directives/tbAuthenticationProviderSelector';
import { IActionContext } from '../helpers/ActionContext';
import { Module } from '../Module';
import { SiteUsers } from '../resources/SiteUsers';
import { DomainFamily } from '../ServerApi/types/IServerSettingsUnauthenticated';
import { ActiveDirectoryGroupAction } from './ActiveDirectoryGroupAction';
import { AddActiveDirectoryUsersAction } from './AddActiveDirectoryUsersAction';
import { AddExternalUsersAction } from './AddExternalUsersAction';
import { AddLocalUserAction } from './AddLocalUserAction';
import { BulkImportUsersAction } from './BulkImportUsersAction';
import { IModalInstance, IModalOptions, ModalService, ModalSize } from './ModalService';
import { ServerService } from './ServerService';
import {
  AuthenticationProviderType,
  IAuthenticationProvider,
  SiteAuthenticationSettingsService,
} from './SiteAuthenticationSettingsService';

// triggered by sub-dialogs when one or more users have been added to the site or server
export let UserAddedEvent = 'UserAddedEvent';

interface IDescriptionTranslationValues {
    SERVER_NAME: string;
    PROVIDER_NAME: string;
    SITE_INVITE_NOTIF_SETTINGS_URL?: string;
}

export class AddUsersOptionsAction {

  public static $inject = ['$translate', 'ModalService', 'ServerService', 'SiteAuthenticationSettingsService'];

  constructor(
    private $translate: ng.translate.ITranslateService,
    private modal: ModalService,
    private serverService: ServerService,
    private siteAuthenticationSettingsService: SiteAuthenticationSettingsService) {
  }

  public getInitialAuthenticationProviderSelectorModel(): IAuthenticationProviderSelectorModel {
    return {
      getProviderDisplayName: (provider: IAuthenticationProvider) => {
        const translateValues = {
          PROVIDER_NAME: this.siteAuthenticationSettingsService.getProviderDisplayName(provider),
        };
        return this.$translate.instant('addUsersWithAuthentication', translateValues);
      },
      getProviderDescription: (provider: IAuthenticationProvider, inviteNotificationEnabledForSite: boolean) => {
        const determineTranslateKey = (): string => {
          let translateKey = null;
          if (provider.type === AuthenticationProviderType.default) {
            if (this.serverService.isTableauIdSupported()) {
              translateKey = 'addUsersWithAuthentication_description_tableauid';
            }
          } else {
            // conditional descriptions for 1) both when the server config for site invite notifications is turned on and off and for 2) Site level settings on/off
            translateKey = this.serverService.isSiteInviteEmailEnabledForServer()
              ? (inviteNotificationEnabledForSite ? 'addUsersWithAuthentication_description_external_email' : 'addUsersWithAuthentication_description_external_email_off')
              : 'addUsersWithAuthentication_description_external';
          }
          return translateKey;
        };

        const buildTranslatonValues = (): IDescriptionTranslationValues => {
          const translateValues: IDescriptionTranslationValues = {
            SERVER_NAME: this.serverService.serverName(),
            PROVIDER_NAME: this.siteAuthenticationSettingsService.getProviderDisplayName(provider)
          };
          if (this.serverService.isSiteInviteEmailEnabledForServer() && !inviteNotificationEnabledForSite) {
            const siteUrls = new SiteUrls({
              siteUrlName: this.serverService.sessionSiteUrlName(),
              queryString: window.location.search
            });
            translateValues['SITE_INVITE_NOTIF_SETTINGS_URL'] = `<a href=${siteUrls.settingsUrl()}>${this.$translate.instant('goToSiteInviteNotifSettings')}</a>`;
          }
          return translateValues;
        };

        const translateKey = determineTranslateKey();
        return translateKey
            ? this.$translate.instant(translateKey, buildTranslatonValues())
            : '';
      },
      authenticationProvider: this.siteAuthenticationSettingsService.getDefaultProvider(),
      authSetting: 'DEFAULT', // should be removed by TFSID:607630
      authenticationProviderIds: undefined
    };
  }

  public showAddSiteUsersOptionsDialog(context: IActionContext): ng.IPromise<any> {
    const dialogInstance = this.createDialogInstance(
      context, AddSiteUsersOptionsDialogCtrl);

    return dialogInstance.result;
  }

  public showAddServerUsersOptionsDialog(context: IActionContext): ng.IPromise<any> {

    const dialogInstance = this.createDialogInstance(
      context, AddServerUsersOptionsDialogCtrl);

    return dialogInstance.result;
  }

  private createDialogInstance(context: IActionContext, controller: any): IModalInstance {

    return this.modal.open({
      scope: context.scope,
      templateUrl: 'addUsersOptionsDialog.html',
      size: ModalSize.fitToContent,
      position: _.extend(ModalService.anchoredTo(context.targetElement)),
      controller: controller,
      resolve: {
        domainFamily: () => this.serverService.domainFamily()
      }
    });
  }

}

Module.instance().service('AddUsersOptionsAction', AddUsersOptionsAction);

// controller

export interface IAddUsersOptionsDialogScope extends ng.IScope {
  title: string;
  // functions tied to the various add buttons
  addLocalUser(): void;
  importAdUser(): void;
  importAdGroup?(): void;
  importFromBulk(): void;
  importExternalUser(): void;
  // from modal dialog
  close: (result: any) => void;
  options: IModalOptions;
}

export class AddSiteUsersOptionsDialogCtrl {

  static $inject = [
    '$scope',
    'AddLocalUserAction',
    'ActiveDirectoryGroupAction',
    'AddActiveDirectoryUsersAction',
    'AddExternalUsersAction',
    'BulkImportUsersAction',
    'domainFamily',
    'SiteUsers'];

  constructor(
    $scope: IAddUsersOptionsDialogScope,
    addLocalUserAction: AddLocalUserAction,
    activeDirectoryGroupAction: ActiveDirectoryGroupAction,
    addActiveDirectoryUsersAction: AddActiveDirectoryUsersAction,
    addExternalUsersAction: AddExternalUsersAction,
    bulkImportUsersAction: BulkImportUsersAction,
    domainFamily: string,
    siteUsers: SiteUsers) {
    $scope.title = 'addUsersToSite';

    if(domainFamily === DomainFamily.Local){
      $scope.addLocalUser = () => {
        // Note: The order here is important, because $scope.close() will destroy the scope.
        siteUsers.getRemainingUserQuota().then(async (availableQuota: number) => {
          await addLocalUserAction.showAddLocalUserDialogForSite($scope.$parent, availableQuota);
          $scope.close({});
        });
      };
    }

    if(domainFamily === DomainFamily.ActiveDirectory){
      $scope.importAdUser = () => {
        // Note: The order here is important, because $scope.close() will destroy the scope.
        siteUsers.getRemainingUserQuota().then(async (availableQuota: number) => {
          await addActiveDirectoryUsersAction.showAddActiveDirectoryUsersToSiteDialog($scope.$parent, availableQuota);
          $scope.close({});
        });
      };
      $scope.importAdGroup = async () => {
        // Note: The order here is important, because $scope.close() will destroy the scope.
        await activeDirectoryGroupAction.showAddAdGroupDialog($scope.$parent);
        $scope.close({});
      };
    }

    if(domainFamily === DomainFamily.External){
      $scope.importExternalUser = () => {
        // Note: The order here is important, because $scope.close() will destroy the scope.
        siteUsers.getRemainingUserQuota().then(async (availableQuota: number) => {
          await addExternalUsersAction.showAddExternalUsersToSiteDialog($scope.$parent, availableQuota);
          $scope.close({});
      });
    };
  }

    $scope.importFromBulk = async () => {
      // Note: The order here is important, because $scope.close() will destroy the scope.
      await bulkImportUsersAction.showBulkImportSiteUsersDialog($scope.$parent);
      $scope.close({});
    };
  }

}

export class AddServerUsersOptionsDialogCtrl {

  static $inject = [
    '$scope',
    'AddLocalUserAction',
    'AddActiveDirectoryUsersAction',
    'BulkImportUsersAction',
    'domainFamily'];

  constructor(
    $scope: IAddUsersOptionsDialogScope,
    addLocalUserAction: AddLocalUserAction,
    addActiveDirectoryUsersAction: AddActiveDirectoryUsersAction,
    bulkImportUsersAction: BulkImportUsersAction,
    domainFamily: string) {
    $scope.title = 'addUsersToServer';

    if (domainFamily === DomainFamily.Local) {
      $scope.addLocalUser = () => {
        // Note: The order here is important, because $scope.close() will destroy the scope.
        addLocalUserAction.showAddLocalUserDialogForServer($scope.$parent);
        $scope.close({});
      };
    }

    if (domainFamily === DomainFamily.ActiveDirectory) {
      $scope.importAdUser = () => {
        // Note: The order here is important, because $scope.close() will destroy the scope.
        addActiveDirectoryUsersAction.showAddActiveDirectoryUsersToServerDialog($scope.$parent);
        $scope.close({});
      };
    }

    $scope.importFromBulk = () => {
      // Note: The order here is important, because $scope.close() will destroy the scope.
      bulkImportUsersAction.showBulkImportServerUsersDialog($scope.$parent);
      $scope.close({});
    };
  }

}

Module.instance().controller('AddSiteUsersOptionsDialogCtrl', AddSiteUsersOptionsDialogCtrl);
Module.instance().controller('AddServerUsersOptionsDialogCtrl', AddServerUsersOptionsDialogCtrl);
